/**
 * Dataset 상세의 데이터 흐름 — 창과 route 페이지가 같은 컨트롤러를 쓴다.
 *
 * 조회 · Probe 폴링 · 접수 · hide/show · 모달 상태를 한 객체가 들고 외부 스토어(`subscribe` /
 * `getSnapshot`)로 낸다. 화면(PageBase 클래스든 창 콘텐츠든)은 스냅샷을 그리고 명령만 부른다.
 *
 * 생성과 활성화 시점이 다르고 타이머 · 진행 중 요청을 소유하므로 `init` / `dispose` 를 두고 반복
 * 호출에 안전하게 만든다(규칙 §8). 세대 번호로 늦게 온 응답을 버린다 — dispose 뒤나 재init 뒤 도착한
 * 응답이 새 상태를 덮으면 안 된다(규칙 §6). `setActive(false)`(창 최소화)는 폴링만 멈추고 상태는 남긴다.
 *
 * Owns:
 * - 스냅샷 · 세대 · 폴러 배선 · 명령의 결과 반영(토스트 포함).
 *
 * Does not own:
 * - 어느 Probe 를 보는가 — 소유자(URL 또는 창 로컬)가 `setCurrentProbe` 로 준다. 섹션 축 — `datasetSections.ts`.
 * - HTTP 모양 — `api/`. 문구 — `datasetFormatters.ts` · `probeStatus.ts`. 이력 표 소스 — 본문(page)이
 *   스냅샷의 `probes` 로 채운다.
 */
import { toast } from "@carbon6/c6-frontend-design";
import { getDataset, hideDataset, showDataset } from "../api/datasetApi";
import { getProbe, listProbes, requestProbe } from "../api/probeApi";
import type { Dataset, LatestProbe } from "../domain/dataset";
import { isProbeOpen, type Probe, type Revision } from "../domain/probe";
import { resolveDatasetErrorMessage } from "./datasetFormatters";
import { ProbePoller } from "./probePoller";
import { publishProbeChange } from "./probeChanges";

// 서버 `DEFAULT_EPISODE_TARGET`. Worker 는 `min(episode_target, SPNP_COLLECT_EPISODES)` 만큼 담는다.
export const DEFAULT_EPISODE_TARGET = 3;

/**
 * 모달 셋은 서로 배타적이라 한 판별 유니온에 모은다 — 두 모달이 동시에 열린 조합이 타입에서 생기지
 * 않는다.
 */
export type DatasetDetailDialogState =
  | { kind: "closed" }
  | {
      kind: "requestProbe";
      episodeTarget: number;
      /** 종료된 Probe 뒤의 재요청인지 — 캡션 문구가 갈린다. */
      isRetry: boolean;
      errorMessage: string | null;
      submitting: boolean;
    }
  | { kind: "hide"; errorMessage: string | null; submitting: boolean };

export const CLOSED_DETAIL_DIALOG: DatasetDetailDialogState = { kind: "closed" };

export type DatasetDetailSnapshot = {
  dataset: Dataset | null;
  /** 이력 — 서버가 최신순으로 준다. */
  probes: readonly Probe[];
  loading: boolean;
  loadErrorMessage: string | null;
  probeErrorMessage: string | null;
  dialog: DatasetDetailDialogState;
  /** 경과 시간 표시의 기준. 폴링 tick 마다 갱신한다. */
  nowMs: number;
  /** 소유자가 고른 Probe. 이력에 없거나 `null` 이면 가장 최근 건이 current 다. */
  currentProbeUuid: string | null;
};

export type DatasetDetailControllerOptions = {
  datasetUuid: string;
  /** 접수 · 이력 클릭으로 볼 Probe 가 바뀔 때 — 소유자가 경로나 창 상태를 옮긴다. */
  onOpenProbe: (probeUuid: string) => void;
  /** 테스트가 시간을 고정할 수 있게 주입한다. */
  now?: () => number;
};

export class DatasetDetailController {
  readonly datasetUuid: string;

  private readonly options: DatasetDetailControllerOptions;
  private readonly now: () => number;
  private readonly listeners = new Set<() => void>();
  private readonly poller: ProbePoller;
  private snapshot: DatasetDetailSnapshot;
  private generation = 0;
  private active = true;
  private disposed = false;

  constructor(options: DatasetDetailControllerOptions) {
    this.options = options;
    this.datasetUuid = options.datasetUuid;
    this.now = options.now ?? (() => Date.now());
    this.snapshot = {
      dataset: null,
      probes: [],
      loading: true,
      loadErrorMessage: null,
      probeErrorMessage: null,
      dialog: CLOSED_DETAIL_DIALOG,
      nowMs: this.now(),
      currentProbeUuid: null,
    };
    this.poller = new ProbePoller({
      fetchProbe: getProbe,
      onProbe: (probe) => this.applyProbe(probe),
      onError: (error) => this.patch({ probeErrorMessage: resolveDatasetErrorMessage(error) }),
      now: this.now,
    });
  }

  readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  readonly getSnapshot = (): DatasetDetailSnapshot => this.snapshot;

  /** 조회를 시작한다. 두 조회는 서로 독립이라 병렬로 보낸다 — 순차로 묶으면 250ms 왕복이 두 번 쌓인다(규칙 §6). */
  async init(): Promise<void> {
    // StrictMode의 setup → cleanup → setup에서도 같은 컨트롤러로 새 조회를 시작한다.
    this.disposed = false;
    const generation = (this.generation += 1);
    this.patch({ loading: true, loadErrorMessage: null, probeErrorMessage: null });

    const [dataset, probes] = await Promise.all([
      getDataset(this.datasetUuid).catch((error: unknown) => {
        if (generation === this.generation) {
          this.patch({ loadErrorMessage: resolveDatasetErrorMessage(error) });
        }
        return null;
      }),
      listProbes(this.datasetUuid).catch((error: unknown) => {
        if (generation === this.generation) {
          this.patch({ probeErrorMessage: resolveDatasetErrorMessage(error) });
        }
        return [] as readonly Probe[];
      }),
    ]);
    if (generation !== this.generation) {
      return;
    }

    this.patch({ dataset, probes, loading: false, nowMs: this.now() });
    this.syncPoller();
  }

  dispose(): void {
    this.disposed = true;
    this.generation += 1;
    // 타이머·리스너·진행 중 응답을 정리하되 다음 init에서 폴링을 다시 시작할 수 있게 한다.
    this.poller.stop();
  }

  /** 창 최소화 같은 비활성 구간 — 폴링만 멈추고 상태는 남긴다. 다시 켜면 이어서 관측한다. */
  setActive(active: boolean): void {
    if (this.active === active) {
      return;
    }
    this.active = active;
    this.syncPoller();
  }

  /** 소유자가 고른 Probe. 폴링 대상도 이 값을 따른다. */
  setCurrentProbe(probeUuid: string | null): void {
    if (this.snapshot.currentProbeUuid === probeUuid) {
      return;
    }
    this.patch({ currentProbeUuid: probeUuid });
    this.syncPoller();
  }

  /** 지금 상세가 보여 주는 Probe — 고른 것이 이력에 없거나 없으면 가장 최근 건(서버가 최신순). */
  currentProbe(): Probe | null {
    return selectProbe(this.snapshot.probes, this.snapshot.currentProbeUuid);
  }

  /** 개요가 말하는 Revision — 가장 최근에 확보한 것(고른 Probe 가 아직 진행 중일 수 있다). */
  currentRevision(): Revision | null {
    return this.snapshot.probes.find((probe) => probe.revision !== null)?.revision ?? null;
  }

  readonly closeDialog = (): void => {
    this.patch({ dialog: CLOSED_DETAIL_DIALOG });
  };

  readonly openRequestProbeDialog = (): void => {
    const probe = this.currentProbe();
    this.patch({
      dialog: {
        kind: "requestProbe",
        episodeTarget: probe?.episodeTarget ?? DEFAULT_EPISODE_TARGET,
        isRetry: probe !== null && !isProbeOpen(probe.state),
        errorMessage: null,
        submitting: false,
      },
    });
  };

  readonly setEpisodeTarget = (episodeTarget: number): void => {
    const { dialog } = this.snapshot;
    if (dialog.kind === "requestProbe") {
      this.patch({ dialog: { ...dialog, episodeTarget } });
    }
  };

  readonly confirmRequestProbe = (): void => {
    const { dialog } = this.snapshot;
    if (dialog.kind !== "requestProbe" || dialog.submitting) {
      return;
    }
    const generation = this.generation;
    this.patch({ dialog: { ...dialog, submitting: true, errorMessage: null } });
    void requestProbe(this.datasetUuid, dialog.episodeTarget)
      .then((outcome) => {
        if (generation !== this.generation) {
          return;
        }
        // 201 은 새 Probe, 200 은 이미 진행 중이던 건이다 — 사용자에게 다른 일이다.
        if (outcome.created) {
          toast.success("Probe 를 접수했습니다.");
        } else {
          toast.info("이미 진행 중인 Probe 가 있습니다. 그 건을 이어서 보여 드립니다.");
        }
        publishProbeChange({
          datasetUuid: this.datasetUuid,
          probe: latestOf(outcome.probe),
          requested: true,
        });
        // 접수 응답이 이력의 정본이다 — 새 건이면 앞에 붙이고 이미 있던 건이면 그 자리를 덮는다.
        const probes = this.snapshot.probes.some((row) => row.uuid === outcome.probe.uuid)
          ? this.snapshot.probes.map((row) =>
              row.uuid === outcome.probe.uuid ? outcome.probe : row,
            )
          : [outcome.probe, ...this.snapshot.probes];
        this.patch({
          dialog: CLOSED_DETAIL_DIALOG,
          probes,
          probeErrorMessage: null,
          nowMs: this.now(),
          currentProbeUuid: outcome.probe.uuid,
        });
        this.options.onOpenProbe(outcome.probe.uuid);
        this.syncPoller();
      })
      .catch((error: unknown) => {
        if (generation !== this.generation) {
          return;
        }
        const current = this.snapshot.dialog;
        if (current.kind === "requestProbe") {
          this.patch({
            dialog: {
              ...current,
              submitting: false,
              errorMessage: resolveDatasetErrorMessage(error),
            },
          });
        }
      });
  };

  readonly openHideDialog = (): void => {
    this.patch({ dialog: { kind: "hide", errorMessage: null, submitting: false } });
  };

  readonly confirmHide = (): void => {
    const { dataset, dialog } = this.snapshot;
    if (dataset === null || dialog.kind !== "hide" || dialog.submitting) {
      return;
    }
    const generation = this.generation;
    this.patch({ dialog: { ...dialog, submitting: true, errorMessage: null } });
    void hideDataset(dataset.uuid)
      .then((next) => {
        if (generation !== this.generation) {
          return;
        }
        toast.success("Dataset 을 목록에서 숨겼습니다.");
        this.patch({ dataset: next, dialog: CLOSED_DETAIL_DIALOG });
      })
      .catch((error: unknown) => {
        if (generation !== this.generation) {
          return;
        }
        const current = this.snapshot.dialog;
        if (current.kind === "hide") {
          this.patch({
            dialog: {
              ...current,
              submitting: false,
              errorMessage: resolveDatasetErrorMessage(error),
            },
          });
        }
      });
  };

  /** 되돌리는 동작이라 확인 없이 즉시 실행한다 — 잃는 것이 없다. */
  readonly show = (): void => {
    const { dataset } = this.snapshot;
    if (dataset === null) {
      return;
    }
    const generation = this.generation;
    void showDataset(dataset.uuid)
      .then((next) => {
        if (generation !== this.generation) {
          return;
        }
        toast.success("Dataset 을 다시 목록에 표시합니다.");
        this.patch({ dataset: next });
      })
      .catch((error: unknown) => {
        toast.error(resolveDatasetErrorMessage(error));
      });
  };

  readonly copySourceRef = (): void => {
    const { dataset } = this.snapshot;
    if (dataset === null) {
      return;
    }
    void navigator.clipboard
      .writeText(dataset.sourceRef)
      .then(() => toast.success("원본 주소를 복사했습니다."))
      .catch(() =>
        toast.warning("원본 주소를 복사하지 못했습니다. 값을 직접 선택해 복사해 주세요."),
      );
  };

  /** 폴링이 준 값을 이력의 그 자리에 덮는다 — 표와 카드가 같은 값을 본다. */
  private applyProbe(probe: Probe): void {
    const probes = this.snapshot.probes.map((row) => (row.uuid === probe.uuid ? probe : row));
    this.patch({ probes, probeErrorMessage: null, nowMs: this.now() });
    publishProbeChange({ datasetUuid: this.datasetUuid, probe: latestOf(probe), requested: false });
  }

  private syncPoller(): void {
    const probe = this.currentProbe();
    if (!this.disposed && this.active && probe !== null && isProbeOpen(probe.state)) {
      this.poller.start(probe.uuid);
      return;
    }
    this.poller.stop();
  }

  private patch(next: Partial<DatasetDetailSnapshot>): void {
    this.snapshot = { ...this.snapshot, ...next };
    for (const listener of this.listeners) {
      listener();
    }
  }
}

/** 경로가 고른 Probe. 이력에 없거나 uuid 가 없으면 가장 최근 건(첫 항목)이다. */
/** 목록에 알릴 Probe 요약. */
function latestOf(probe: Probe): LatestProbe {
  return { uuid: probe.uuid, state: probe.state, failureCode: probe.failureCode };
}

export function selectProbe(probes: readonly Probe[], probeUuid: string | null): Probe | null {
  if (probeUuid !== null) {
    const found = probes.find((probe) => probe.uuid === probeUuid);
    if (found !== undefined) {
      return found;
    }
  }
  return probes[0] ?? null;
}
